import { Button } from "@/modules/shadcn/ui/button";
import { ShieldOff } from "lucide-react";
import { useTranslation } from "react-i18next";

export interface LocalAccessDisabledProps {
  /** The address the project still holds, so the user can recognise it. */
  cameraApiUrl: string;
  onOpenSettings?: () => void;
  /**
   * Tightens spacing and type so the screen fits inside the 16:9 preview box
   * (Run → Inference) instead of owning the whole page (Capture).
   */
  compact?: boolean;
}

/**
 * The project reaches its camera by local address, and the organization is no
 * longer permitted to use one.
 *
 * Distinct from "no camera detected": nothing is broken at the camera end, and
 * the fix is to pick a device rather than to check a cable. Reporting it as a
 * detection failure left the owner of a project that had simply stopped working
 * with nothing to look at — the address field is hidden once the flag is off.
 */
export const LocalAccessDisabled = ({
  cameraApiUrl,
  onOpenSettings,
  compact,
}: LocalAccessDisabledProps) => {
  const { t } = useTranslation("camera");
  const cx = {
    root: compact
      ? "flex h-full flex-1 flex-col items-center justify-center overflow-y-auto p-6 text-center"
      : "flex flex-1 flex-col items-center justify-center p-8",
    icon: compact
      ? "mb-4 h-14 w-14 [&_svg]:size-6"
      : "mb-6 h-20 w-20 [&_svg]:size-8",
    title: compact ? "mb-1 text-base" : "mb-2 text-xl",
    description: compact ? "mb-4 text-xs" : "mb-6 text-sm",
    address: compact ? "mb-4 text-xs" : "mb-6 text-sm",
    button: compact ? "mb-4" : "mb-8",
    link: compact ? "text-xs" : "text-sm",
  };

  return (
    <div className={cx.root}>
      <div
        className={`flex items-center justify-center rounded-full bg-black/5 [&_svg]:text-black/60 ${cx.icon}`}
      >
        <ShieldOff />
      </div>

      <p className={`font-semibold text-black ${cx.title}`}>
        {t("connection.localAccessDisabled.title")}
      </p>

      <p className={`text-black/60 ${cx.description}`}>
        {t("connection.localAccessDisabled.description")}
      </p>

      <p className={`rounded-md bg-black/5 px-3 py-1.5 font-mono text-black/45 ${cx.address}`}>
        {cameraApiUrl}
      </p>

      {onOpenSettings && (
        <Button
          onClick={onOpenSettings}
          size={compact ? "sm" : "default"}
          className={cx.button}
        >
          {t("connection.localAccessDisabled.openSettings")}
        </Button>
      )}

      <a
        href="https://robopipe.gitbook.io/doc/getting-started/connection"
        target="_blank"
        rel="noopener noreferrer"
        className={`text-center text-primary hover:underline ${cx.link}`}
      >
        {t("connection.learnMore")}
      </a>
    </div>
  );
};
